// Content Pack Types
export enum PackType {
  Image = 'image',
  Prompt = 'prompt',
  Social = 'social'
}

export enum ExportFormat {
  JSON = 'json',
  ZIP = 'zip',
  Markdown = 'markdown',
  Text = 'text'
}

export enum SocialPlatform {
  Instagram = 'Instagram',
  Twitter = 'Twitter',
  LinkedIn = 'LinkedIn',
  Facebook = 'Facebook',
  TikTok = 'TikTok'
}

// Base interfaces
export interface ContentPackBase {
  sessionId: string;
  packType: PackType;
  title: string;
  description: string;
  category?: string;
  tags?: string[];
  suggestedPrice?: number;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, any>;
}

// Image Pack
export interface CreateImagePackRequest {
  title: string;
  description: string;
  category?: string;
  theme?: string;
  style?: string;
  suggestedPrice?: number;
  tags?: string[];
  aspectRatio?: string;
  resolution?: string;
}

export interface ImagePackCreationResponse {
  sessionId: string;
  message?: string;
  packDetails?: ImagePackDetails;
  // Support actual API response format
  prompts?: string[] | ImagePrompt[];
  status?: string;
  title?: string;
  description?: string;
  category?: string;
  tags?: string[];
  price?: number;
  pack?: {
    title?: string;
    description?: string;
    category?: string;
    tags?: string[];
    price?: number;
    prompts?: string[] | ImagePrompt[];
    posts?: any[];
    images?: any[];
    packType?: string;
    theme?: string;
    style?: string;
    dimensions?: { aspectRatio?: string };
    createdAt?: string;
    updatedAt?: string;
  };
}

export interface ImagePackDetails {
  title: string;
  description: string;
  category?: string;
  theme?: string;
  style?: string;
  aspectRatio?: string;
  resolution?: string;
  imageCount: number;
  prompts: ImagePrompt[];
  tags?: string[];
  suggestedPrice?: number;
  metadata?: Record<string, any>;
}

export interface ImagePrompt {
  id: string;
  prompt: string;
  variations?: string[];
  imageUrl?: string;
  metadata?: Record<string, any>;
}

export interface GeneratePromptsRequest {
  count: number;
  includeVariations?: boolean;
  temperature?: number;
  maxTokens?: number;
}

export interface UpdateImagePackRequest {
  title?: string;
  description?: string;
  prompts?: ImagePrompt[];
  metadata?: Record<string, any>;
}

export interface GenerateImagesRequest {
  promptIds?: string[];
  generateAll?: boolean;
  provider?: string;
  quality?: string;
}

// Prompt Pack
export interface CreatePromptPackRequest {
  title: string;
  description: string;
  category?: string;
  suggestedPrice?: number;
  tags?: string[];
  targetModels?: string[];
  complexity?: string;
}

export interface PromptPackCreationResponse {
  sessionId: string;
  message?: string;
  packDetails?: PromptPackDetails;
  // Support actual API response format
  prompts?: string[] | PromptItem[];
  status?: string;
  title?: string;
  description?: string;
  category?: string;
  tags?: string[];
  price?: number;
  packType?: string;
  pack?: {
    title?: string;
    description?: string;
    prompts?: string[] | PromptItem[];
    packType?: string;
    createdAt?: string;
    updatedAt?: string;
  };
}

export interface PromptPackDetails {
  title: string;
  description: string;
  category?: string;
  targetModels?: string[];
  complexity?: string;
  promptCount: number;
  prompts: PromptItem[];
  tags?: string[];
  suggestedPrice?: number;
  metadata?: Record<string, any>;
}

export interface PromptItem {
  id: string;
  title: string;
  prompt: string;
  category?: string;
  tags?: string[];
  testResults?: TestResult[];
}

export interface TestResult {
  model: string;
  output: string;
  quality: number;
  timestamp: string;
}

export interface AddPromptsRequest {
  prompts: {
    title: string;
    prompt: string;
    category?: string;
    tags?: string[];
  }[];
}

export interface TestPromptRequest {
  promptId: string;
  model?: string;
  temperature?: number;
  maxTokens?: number;
}

// Social Pack
export interface CreateSocialPackRequest {
  title: string;
  description: string;
  category?: string;
  industry?: string;
  suggestedPrice?: number;
  tags?: string[];
  platforms?: SocialPlatform[];
  campaignDuration?: number;
  targetAudience?: string;
}

export interface SocialPackCreationResponse {
  sessionId: string;
  message?: string;
  packDetails?: SocialPackDetails;
  // Support actual API response format
  posts?: SocialPost[];
  status?: string;
  title?: string;
  description?: string;
  category?: string;
  tags?: string[];
  price?: number;
  packType?: string;
  pack?: {
    title?: string;
    description?: string;
    posts?: SocialPost[];
    packType?: string;
    createdAt?: string;
    updatedAt?: string;
  };
}

export interface SocialPackDetails {
  title: string;
  description: string;
  category?: string;
  industry?: string;
  platforms: SocialPlatform[];
  campaignDuration?: number;
  targetAudience?: string;
  postCount: number;
  posts: SocialPost[];
  tags?: string[];
  suggestedPrice?: number;
  metadata?: Record<string, any>;
}

export interface SocialPost {
  id: string;
  platform: SocialPlatform;
  content: string;
  hashtags: string[];
  mediaType?: string;
  scheduledTime?: string;
  engagement?: EngagementMetrics;
}

export interface EngagementMetrics {
  estimatedReach: number;
  estimatedEngagement: number;
  bestPostingTime?: string;
}

export interface GeneratePostsRequest {
  platforms: SocialPlatform[];
  postsPerPlatform: number;
  tone?: string;
  includeHashtags?: boolean;
  temperature?: number;
}

export interface UpdateSocialPackPostsRequest {
  posts: SocialPost[];
}

export interface UpdateMetadataRequest {
  metadata: Record<string, any>;
}

// Common interfaces
export interface ExportPackRequest {
  format: ExportFormat;
  includeGeneratedContent?: boolean;
  compress?: boolean;
}

export interface ImportPackRequest {
  packData: string;
  format: ExportFormat;
}

export interface ImportPackResponse {
  sessionId: string;
  packType: PackType;
  message: string;
  importedItems: number;
}

export interface PackStatusResponse {
  sessionId: string;
  packType?: PackType;
  status: string;
  progress?: number;
  itemsCompleted?: number;
  totalItems?: number;
  errors?: string[];
  // Support actual API response format
  prompts?: string[] | any[];
  posts?: any[];
  images?: any[];
  title?: string;
  description?: string;
  category?: string;
  tags?: string[];
  theme?: string;
  style?: string;
  pack?: {
    prompts?: string[] | any[];
    posts?: any[];
    images?: any[];
    style?: string;
  };
}

// Session management
export interface ContentPackSession {
  sessionId: string;
  packType: PackType;
  packDetails: ImagePackDetails | PromptPackDetails | SocialPackDetails;
  status: string;
  progress: number;
  createdAt: string;
  updatedAt: string;
}